import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, CheckCircle, Keyboard, RefreshCw, Save, X } from 'lucide-react';
import MainService from '../../services/MainService';
import { ORDER_ENTRY_TAB_FIELDS } from '../../utils/orderEntryTabSettings';

const toRows = (response) => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.Data)) return response.Data;
  if (Array.isArray(response?.data)) return response.data;
  return [];
};

const SECTIONS = [
  {
    title: 'Order header',
    keys: ['voucherNo', 'storeRefNo', 'pickUpBy', 'payType', 'chargeFrom', 'clientBillNo', 'type', 'agent', 'forPickup', 'exchange', 'fragile'],
  },
  {
    title: 'Client',
    keys: ['clientName', 'clientNo', 'clientMobile', 'clientEmirate', 'clientLocation', 'clientLocationUrl', 'clientAddress', 'clientPoBox'],
  },
  {
    title: 'Customer',
    keys: ['customerName', 'customerMobile1', 'customerMobile2', 'customerEmirate', 'customerLocation', 'customerLocationUrl', 'customerAddress', 'customerPoBox'],
  },
  {
    title: 'Details & financial',
    keys: ['remarks', 'quantity', 'pickupDate', 'deliveryDate', 'productPrice', 'deliveryCharge'],
  },
];

const OrderEntryTabSettings = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (!error) return undefined;
    const t = setTimeout(() => setError(''), 5000);
    return () => clearTimeout(t);
  }, [error]);

  useEffect(() => {
    if (!success) return undefined;
    const t = setTimeout(() => setSuccess(''), 3000);
    return () => clearTimeout(t);
  }, [success]);

  const normalizeRows = useCallback((list) => {
    const byKey = new Map();
    (list || []).forEach((item) => {
      if (item?.FieldKey) byKey.set(item.FieldKey, item);
    });

    return ORDER_ENTRY_TAB_FIELDS.map((def) => {
      const existing = byKey.get(def.fieldKey);
      return {
        Id: existing?.Id || 0,
        FieldKey: def.fieldKey,
        Label: existing?.Label || def.label,
        TabOrder: existing?.TabOrder ?? def.tabOrder,
        IsSkipped: Boolean(existing?.IsSkipped),
        IsActive: existing?.IsActive !== false,
      };
    }).sort((a, b) => a.TabOrder - b.TabOrder);
  }, []);

  const loadRows = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await MainService.GetOrderEntryTabSettings();
      setRows(normalizeRows(toRows(response)));
    } catch (err) {
      console.error('Load order entry tab settings error:', err);
      setError('Failed to load order entry tab settings');
      setRows(normalizeRows([]));
    } finally {
      setLoading(false);
    }
  }, [normalizeRows]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  const skippedCount = useMemo(() => rows.filter((r) => r.IsSkipped).length, [rows]);
  const stopCount = rows.length - skippedCount;

  const setSkipped = (fieldKey, skipped) => {
    setRows((prev) =>
      prev.map((row) =>
        row.FieldKey === fieldKey ? { ...row, IsSkipped: skipped } : row
      )
    );
  };

  const setAllSkipped = (skipped) => {
    setRows((prev) => prev.map((row) => ({ ...row, IsSkipped: skipped })));
  };

  const setSectionSkipped = (keys, skipped) => {
    const keySet = new Set(keys);
    setRows((prev) =>
      prev.map((row) =>
        keySet.has(row.FieldKey) ? { ...row, IsSkipped: skipped } : row
      )
    );
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');
      const payload = rows.map((row) => ({
        Id: row.Id || 0,
        FieldKey: row.FieldKey,
        Label: row.Label,
        TabOrder: row.TabOrder,
        IsSkipped: Boolean(row.IsSkipped),
        IsActive: true,
      }));
      const response = await MainService.SaveOrderEntryTabSettings(payload);
      if (response === 'Success' || response === true || response === 'true') {
        setSuccess('Saved — Order Entry will use these Tab settings');
        await loadRows();
      } else {
        setError(response || 'Failed to save settings');
      }
    } catch (err) {
      console.error('Save order entry tab settings error:', err);
      setError('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const groups = useMemo(
    () =>
      SECTIONS.map((section) => ({
        ...section,
        rows: rows.filter((r) => section.keys.includes(r.FieldKey)),
      })),
    [rows]
  );

  return (
    <div className="space-y-6">
      {error && (
        <div className="fixed top-4 right-4 z-50 flex min-w-[300px] items-center gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-700 shadow-lg">
          <AlertTriangle className="h-5 w-5 shrink-0" />
          <span className="flex-1">{error}</span>
          <button type="button" onClick={() => setError('')} className="text-red-500 hover:text-red-700">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}
      {success && (
        <div className="fixed top-4 right-4 z-50 flex min-w-[300px] items-center gap-3 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-green-700 shadow-lg">
          <CheckCircle className="h-5 w-5 shrink-0" />
          <span className="flex-1">{success}</span>
          <button type="button" onClick={() => setSuccess('')} className="text-green-500 hover:text-green-700">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
        <div className="mb-5 flex flex-wrap items-start justify-between gap-4">
          <div className="max-w-2xl">
            <div className="flex items-center gap-2">
              <Keyboard className="h-6 w-6 text-[#46955A]" />
              <h1 className="text-2xl font-bold text-gray-900">Order Entry Tab</h1>
            </div>
            <p className="mt-2 text-sm text-gray-600">
              Choose what happens when the user presses{' '}
              <kbd className="rounded border border-gray-300 bg-gray-50 px-1.5 py-0.5 font-sans text-xs font-semibold text-gray-700">
                Tab
              </kbd>{' '}
              on Create Order.
            </p>
            <ul className="mt-2 space-y-1 text-sm text-gray-500">
              <li>
                <span className="font-medium text-[#46955A]">Stop</span> — cursor moves to this field
              </li>
              <li>
                <span className="font-medium text-gray-600">Skip</span> — cursor jumps past this field
              </li>
            </ul>
            <p className="mt-3 text-xs text-gray-400">
              Applies to everyone in the company · {stopCount} stop · {skippedCount} skip
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => setAllSkipped(false)}
              disabled={loading || saving}
              className="rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              Stop on all
            </button>
            <button
              type="button"
              onClick={() => setAllSkipped(true)}
              disabled={loading || saving}
              className="rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              Skip all
            </button>
            <button
              type="button"
              onClick={loadRows}
              disabled={loading || saving}
              className="inline-flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={loading || saving}
              className="inline-flex items-center gap-2 rounded-lg bg-[#46955A] px-4 py-2 text-sm font-medium text-white hover:bg-[#3d7d4f] disabled:opacity-50"
            >
              {saving ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Save
            </button>
          </div>
        </div>

        {loading && rows.length === 0 ? (
          <div className="flex items-center justify-center gap-2 py-16 text-gray-500">
            <RefreshCw className="h-5 w-5 animate-spin" />
            Loading…
          </div>
        ) : (
          <div className="space-y-5">
            {groups.map((group) => {
              const sectionSkipped = group.rows.filter((r) => r.IsSkipped).length;
              return (
                <div key={group.title} className="overflow-hidden rounded-xl border border-gray-200">
                  <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 bg-gray-50 px-4 py-2.5">
                    <div>
                      <h2 className="text-sm font-semibold text-gray-800">{group.title}</h2>
                      <p className="text-xs text-gray-400">
                        {group.rows.length - sectionSkipped} stop · {sectionSkipped} skip
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => setSectionSkipped(group.keys, false)}
                        disabled={loading || saving}
                        className="text-xs font-medium text-[#46955A] hover:underline disabled:opacity-50"
                      >
                        Stop all
                      </button>
                      <span className="text-xs text-gray-300">|</span>
                      <button
                        type="button"
                        onClick={() => setSectionSkipped(group.keys, true)}
                        disabled={loading || saving}
                        className="text-xs font-medium text-gray-500 hover:underline disabled:opacity-50"
                      >
                        Skip all
                      </button>
                    </div>
                  </div>

                  <div className="divide-y divide-gray-100">
                    {group.rows.map((row) => {
                      const stops = !row.IsSkipped;
                      return (
                        <div
                          key={row.FieldKey}
                          className={`flex flex-wrap items-center justify-between gap-3 px-4 py-3 ${
                            stops ? 'bg-white' : 'bg-gray-50/80'
                          }`}
                        >
                          <p
                            className={`min-w-0 text-sm font-medium ${
                              stops ? 'text-gray-900' : 'text-gray-500'
                            }`}
                          >
                            {row.Label}
                          </p>

                          <div
                            className="inline-flex shrink-0 rounded-lg border border-gray-200 bg-white p-0.5"
                            role="group"
                            aria-label={`${row.Label} Tab behavior`}
                          >
                            <button
                              type="button"
                              onClick={() => setSkipped(row.FieldKey, false)}
                              disabled={loading || saving}
                              className={`rounded-md px-3 py-1.5 text-xs font-semibold transition-colors disabled:opacity-50 ${
                                stops
                                  ? 'bg-[#46955A] text-white shadow-sm'
                                  : 'text-gray-500 hover:text-gray-800'
                              }`}
                            >
                              Stop
                            </button>
                            <button
                              type="button"
                              onClick={() => setSkipped(row.FieldKey, true)}
                              disabled={loading || saving}
                              className={`rounded-md px-3 py-1.5 text-xs font-semibold transition-colors disabled:opacity-50 ${
                                !stops
                                  ? 'bg-gray-700 text-white shadow-sm'
                                  : 'text-gray-500 hover:text-gray-800'
                              }`}
                            >
                              Skip
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default OrderEntryTabSettings;
